import React, { useCallback, useMemo } from "react";
import { OrgHomeNavigationProp, OrgHomeRouteKey } from "./OrgHomeNavigation";

import {
  AHIcon,
  AHIconType,
  AHSeparator,
  AHText,
  Box,
  ListItem,
  TouchableBox,
  useBoxStyle,
  useLayoutContext,
  useTheme,
  useTime,
  Image,
} from "arkh";
import { graphql, useFragment } from "react-relay";

import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Color from "color";
import { FlatList, Platform, View } from "react-native";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { WorkspaceSelector } from "./WorkspaceSelector/WorkspaceSelector";
import {
  OrgFeatures,
  OrgHomeMasterComponent_query$key,
} from "./__generated__/OrgHomeMasterComponent_query.graphql";

import { OrgLogoutButton } from "./OrgLogoutButton";

import { WorkspaceBreadcrumbs } from "./WorkspaceSelector/WorkspaceBreadcrumbs";
import { useCalendars } from "expo-localization";
import { TimeZoneProvider, useTimeZone } from "../../OS/Timezone";
import { FormattedMessage } from "react-intl";
import { DateTime } from "luxon";

type OrgHomeMasterComponentProps = {
  navigation: OrgHomeNavigationProp<"OrgSpaces">;
  selectedRoute?: OrgHomeRouteKey | undefined;
  query: OrgHomeMasterComponent_query$key;
  onSelectWorkspace: (workspaceId: string) => void;
  workspaceId: string;
};

export function OrgHomeMasterComponent(props: OrgHomeMasterComponentProps) {
  const isCompact =
    useSizeClass().width === SizeClass.COMPACT && Platform.OS !== "web";
  const insets = useSafeAreaInsets();
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();
  const theme = useTheme();
  const data = useFragment(
    graphql`
      fragment OrgHomeMasterComponent_query on Query {
        organisation @required(action: THROW) {
          name
          logo {
            id
            ...Image_image
          }
          features
          hasAdminAccess
        }
        viewer {
          workspaces {
            id
            timezone
            ...WorkspaceSelector_workspaces
            ...WorkspaceBreadcrumbs_workspaces
          }
        }

        me {
          email
          ...OrgLogoutButton_me
        }
      }
    `,
    props.query
  );

  const isDev = __DEV__ || data?.me?.email?.endsWith("@storesprint.app");

  const _ld: (
    | {
        title: string;
        key: OrgHomeRouteKey;
        icon: AHIconType;
        onPress: () => void;
        dev?: boolean;
        devOnly?: boolean;
        featureKey?: OrgFeatures;
        type: "FEATURE";
      }
    | {
        label: string;
        type: "HEADER";
        dev?: boolean;
        devOnly?: boolean;
        featureKey?: OrgFeatures;
      }
  )[] = useMemo(
    () => [
      {
        label: "Teleport",
        type: "HEADER" as const,
      },
      {
        title: intl_tokens.Units,
        key: "OrgStores",
        onPress: () => {
          props.navigation.navigateDeprecated("OrgStores", {});
        },
        icon: "store",
        type: "FEATURE" as const,
      },
      {
        label: "Operations",
        type: "HEADER" as const,
      },

      {
        title: intl_tokens.Checklists,
        key: "WorkspaceRoutines",
        onPress: () => {
          props.navigation.navigateDeprecated("WorkspaceRoutines", {
            screen: "WorkspaceRoutinesMain",
          });
        },
        icon: "routine",
        featureKey: "ROUTINES",
        type: "FEATURE" as const,
      },
      {
        title: intl_tokens.Todos,
        key: "WorkspaceTodos",
        icon: "check",
        onPress: () => {
          props.navigation.navigateDeprecated("WorkspaceTodos", {
            screen: "WorkspaceTodosMain",
          });
        },
        featureKey: "TODOS",
        type: "FEATURE" as const,
      },
      {
        title: intl_tokens.Signals,
        key: "WorkspaceSignals",
        onPress: () => {
          props.navigation.navigateDeprecated("WorkspaceSignals", {
            screen: "WorkspaceSignalsMain",
          });
        },
        icon: "signal",
        featureKey: "SIGNALS",
        type: "FEATURE" as const,
      },
      {
        title: intl_tokens.Files,
        key: "WorkspaceFiles",
        onPress: () => {
          props.navigation.navigateDeprecated("WorkspaceFiles", {});
        },
        icon: "files",
        featureKey: "FILES",
        type: "FEATURE" as const,
      },
      {
        label: "Storecast",
        type: "HEADER" as const,
      },
      {
        title: intl_tokens.Spaces,
        key: "OrgSpaces",
        onPress: () => {
          props.navigation.navigateDeprecated("OrgSpaces", {});
        },
        icon: "widget",
        featureKey: "SPACES",
        type: "FEATURE" as const,
      },
      {
        type: "HEADER" as const,
        label: "Operational Intelligence",
        featureKey: "ANALYTICS",
        dev: true,
      },
      {
        type: "FEATURE" as const,
        title: intl_tokens.Analytics,
        featureKey: "ANALYTICS",
        key: "WorkspaceAnalytics",
        icon: "analytics",
        dev: true,

        onPress: () => {
          props.navigation.navigateDeprecated("WorkspaceAnalytics", {});
        },
      },

      /*  {
        type: "HEADER" as const,
        label: intl_tokens.Products,
        featureKey: "PRODUCTS",
      },
      {
        type: "FEATURE" as const,
        title: intl_tokens.Products,
        featureKey: "PRODUCTS",
        key: "WorkspaceProducts",
        icon: "product",
        onPress: () => {
          props.navigation.navigateDeprecated("WorkspaceProducts", {});
        },
      }, */
    ],
    [
      intl_tokens.Analytics,
      intl_tokens.Checklists,
      intl_tokens.Files,
      intl_tokens.Signals,
      intl_tokens.Spaces,
      intl_tokens.Todos,
      intl_tokens.Units,
      props.navigation,
    ]
  );

  const listData = useMemo(
    () =>
      _ld.filter((item) => {
        if (item.dev && isDev) {
          return true;
        }
        if (item.devOnly) {
          return false;
        }

        if (item.type === "HEADER") {
          if (
            item.featureKey &&
            !data.organisation.features.includes(item.featureKey)
          ) {
            return false;
          }
          return true;
        }

        if (
          item.featureKey &&
          !data.organisation.features.includes(item.featureKey)
        ) {
          return false;
        }
        return true;
      }),
    [_ld, data.organisation.features, isDev]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      if (item.type === "HEADER") {
        return (
          <Box spaceHorizontal="large" spaceTop="large" spaceBottom="small">
            <AHText type="subtitle" color="text-washed">
              {item.label}
            </AHText>
          </Box>
        );
      }

      const tintColor = isCompact
        ? "text-primary"
        : props.selectedRoute === item.key
          ? "primary"
          : "text-secondary";
      return (
        <Box spaceHorizontal="medium">
          <ListItem
            left={
              <Box
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  width: 18,
                }}
              >
                <AHIcon type={item.icon} color={tintColor} />
              </Box>
            }
            onPress={item.onPress}
            isFirst={!isCompact || index === 0}
            isLast={!isCompact || index === listData.length - 1}
            isSelected={props.selectedRoute === item.key}
            selectedBackgroundColor={Color(theme.primary)
              .alpha(0.07)
              .toString()}
            right={isCompact ? <ListItem.NavArrow /> : undefined}
          >
            <ListItem.Title color={tintColor}>{item.title}</ListItem.Title>
          </ListItem>
        </Box>
      );
    },
    [isCompact, listData.length, props, theme.primary]
  );
  const contentContainerStyle = useBoxStyle({
    color: isCompact ? "surface-background" : "surface-primary",
    spaceBottom: "large",
  });

  const workspaceTimezone = useMemo(
    () =>
      data.viewer?.workspaces?.find(
        (workspace) => workspace.id === props.workspaceId
      )?.timezone,
    [data.viewer?.workspaces, props.workspaceId]
  );

  const ListHeaderComponent = useMemo(
    () => (
      <>
        <Box
          spaceBottom="large"
          spaceHorizontal="large"
          color="surface-primary"
          style={{
            paddingTop: insets.top + layout.space.large,
          }}
        >
          {data.organisation.hasAdminAccess ? (
            <TouchableBox
              style={{ flexDirection: "row", alignItems: "center" }}
              spaceBottom="large"
              onPress={() =>
                props.navigation.navigateDeprecated("OrgAdmin", {})
              }
            >
              <AHIcon
                spaceRight="xsmall"
                size="xsmall"
                color="text-secondary"
                type="settings"
              />
              <AHText type="subtitle" color="text-secondary">
                {intl_tokens.OrgAdmin}
              </AHText>
            </TouchableBox>
          ) : null}
          {data.organisation.logo ? (
            <Image
              type="remote"
              contentFit="contain"
              contentPosition="left center"
              image={data.organisation.logo}
              style={{
                height: 40,
                width: "100%",
                marginBottom: layout.space.medium,
              }}
            />
          ) : null}
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
            spaceBottom="small"
          >
            <WorkspaceBreadcrumbs
              workspaces={data.viewer?.workspaces ?? []}
              currentWorkspaceId={props.workspaceId}
            />
          </Box>
          <WorkspaceSelector
            workspaces={data.viewer?.workspaces ?? []}
            workspaceId={props.workspaceId}
            onSelectWorkspace={props.onSelectWorkspace}
          />
          {workspaceTimezone ? (
            <WorkspaceTimezoneIndicator workspaceTimezone={workspaceTimezone} />
          ) : null}
        </Box>
        <AHSeparator />
      </>
    ),
    [
      data.organisation.hasAdminAccess,
      data.viewer?.workspaces,
      insets.top,
      intl_tokens.OrgAdmin,
      layout.space.large,
      props.navigation,
      props.onSelectWorkspace,
      props.workspaceId,
      workspaceTimezone,
      layout.space.medium,
      data.organisation.logo,
    ]
  );

  return (
    <>
      <FlatList
        contentContainerStyle={contentContainerStyle}
        ListHeaderComponent={ListHeaderComponent}
        ItemSeparatorComponent={isCompact ? AHSeparator : undefined}
        stickyHeaderIndices={[0]}
        data={listData}
        renderItem={renderItem}
      />
      <Box
        insets={{
          bottom:
            Platform.OS === "web"
              ? layout.space.medium
              : Math.max(insets.bottom, layout.space.medium),
        }}
        spaceHorizontal="medium"
      >
        {data.me ? (
          <>
            <OrgLogoutButton me={data.me} />
          </>
        ) : null}
      </Box>
    </>
  );
}

function Clock() {
  const time = useTime(6000);

  const tz = useTimeZone();
  return time.setZone(tz).toLocaleString(DateTime.TIME_24_SIMPLE);
}

function Timezone() {
  return useTimeZone();
}

function TimezoneIndicator() {
  return (
    <Box
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        flex: 1,
      }}
      color={"surface-backgroundControl"}
      space="xsmall"
      spaceHorizontal="small"
      cornerRadius="small"
    >
      <AHText
        color="text-secondary"
        style={{
          direction: "rtl",
        }}
        numberOfLines={1}
        ellipsizeMode="head"
      >
        <Timezone />
      </AHText>
      <Box style={{ flexDirection: "row", alignItems: "center" }}>
        <AHText spaceLeft="xsmall" color="text-secondary">
          <Clock />
        </AHText>
        <AHIcon
          spaceLeft="xsmall"
          type="timezone"
          size="xsmall"
          color="text-secondary"
        />
      </Box>
    </Box>
  );
}

function WorkspaceTimezoneIndicator(props: { workspaceTimezone: string }) {
  const calendars = useCalendars();
  const layout = useLayoutContext();
  const userTimezone = useMemo(() => {
    return calendars[0]?.timeZone?.toLocaleLowerCase();
  }, [calendars]);

  return (
    <TimeZoneProvider timezone={props.workspaceTimezone}>
      <Box spaceTop="small">
        {props.workspaceTimezone.toLocaleLowerCase() !== userTimezone ? (
          <Box
            borderColor="border-primary"
            cornerRadius="medium"
            style={{
              borderWidth: 1,
              marginHorizontal: -layout.space.medium,
            }}
            color="surface-primary"
            space="medium"
          >
            <AHText spaceBottom="small" type="body" style={{ flex: 1 }}>
              <FormattedMessage
                defaultMessage="Date and time is displayed in workspace timezone"
                description="OrgHome timezone indicator description"
              />
            </AHText>
            <View
              style={{
                flexDirection: "row",
                marginHorizontal: -layout.space.small,
              }}
            >
              <TimezoneIndicator />
            </View>
          </Box>
        ) : (
          <View
            style={{
              flexDirection: "row",
              marginHorizontal: -layout.space.small,
            }}
          >
            <TimezoneIndicator />
          </View>
        )}
      </Box>
    </TimeZoneProvider>
  );
}
